import { useCallback } from "react";
import { child, get, type DatabaseReference } from "firebase/database";
import { GQL } from "@binale-tech/shared";
import QuickLRU from "quick-lru";

import { getPKRecordsRef } from "@domain/api/firebase/firebaseRootRefs";
import { type GenericRecord } from "@domain/models/GenericRecord";
import { useRecordsInitializers } from "@domain/context/accountingData/useRecordsInitializers";

const DEFAULT_GROUP = "default";

export type RecordPath = {
    productKey: GQL.IProductKey;
    companyId: string;
    groupId?: string | null;
    year: number;
    recordId: string;
};

type Initializers = ReturnType<typeof useRecordsInitializers>;
type Initializer = Initializers[keyof Initializers];

// Maps each accounting product to its record-initializer factory. The Firebase
// ref is derived from `getPKRecordsRef(productKey)` - the unified
// `records/{product_key}/…` root replaces the prior switch-on-ref pattern.
const getProductInit = (productKey: GQL.IProductKey, inits: Initializers): Initializer | null => {
    switch (productKey) {
        case GQL.IProductKey.Er:
            return inits.erInit;
        case GQL.IProductKey.ErA:
            return inits.erAInit;
        case GQL.IProductKey.Deb:
            return inits.debInit;
        case GQL.IProductKey.Fe:
            return inits.feInit;
        case GQL.IProductKey.La:
            return inits.laInit;
        case GQL.IProductKey.Pos:
            return inits.posInit;
        case GQL.IProductKey.Bank:
            return inits.bankInit;
        case GQL.IProductKey.Kb:
            return inits.kbInit;
        default:
            return null;
    }
};

// Module-level dedup: many consumers (popover badges, saldo provider, payment
// source lookups) can ask for the same record path concurrently. Cache the
// in-flight promise so each {companyId, product, group, year, id} is fetched
// at most once. Keys include companyId so cross-company contamination is
// impossible - no need to clear on company switch; old entries simply evict.
//
// Bounded via LRU so the cache can't grow unboundedly over a long session.
// 4096 entries is generous: a single user rarely looks up that many distinct
// out-of-window records, and each entry is one Promise reference (~tens of bytes).
const FETCH_CACHE_SIZE = 4096;
const fetchCache = new QuickLRU<string, Promise<GenericRecord | null>>({ maxSize: FETCH_CACHE_SIZE });

const buildCacheKey = (path: RecordPath) =>
    `${path.companyId}:${path.productKey}:${path.groupId ?? DEFAULT_GROUP}:${path.year}:${path.recordId}`;

/** Clears the module-level fetch cache. Intended for tests; production code
 *  relies on LRU eviction + {companyId, …} keying. */
export const clearRecordFetchCache = () => {
    fetchCache.clear();
};

/**
 * Returns a stable callable that fetches a single accounting record from
 * Firebase using the year-partitioned path
 * `{root}/{companyId}/{groupId|"default"}/{year}/{recordId}`. Resolves to
 * `null` if the record doesn't exist.
 *
 * Hook form (rather than a plain function) so we can reuse the project-wide
 * `useRecordsInitializers` instead of duplicating the Record* constructor map.
 * The returned callable has stable identity for the lifetime of the consumer.
 */
export const useFetchFirebaseRecordByPath = () => {
    const inits = useRecordsInitializers();
    return useCallback(
        (path: RecordPath): Promise<GenericRecord | null> => {
            const init = getProductInit(path.productKey, inits);
            if (!init) {
                return Promise.resolve(null);
            }
            const ref: DatabaseReference = getPKRecordsRef(path.productKey);
            const key = buildCacheKey(path);
            const cached = fetchCache.get(key);
            if (cached) {
                return cached;
            }
            const groupId = path.groupId ?? DEFAULT_GROUP;
            const promise = Promise.resolve().then(async () => {
                const snap = await get(child(ref, `${path.companyId}/${groupId}/${path.year}/${path.recordId}`));
                if (!snap.exists()) {
                    return null;
                }
                return init(snap);
            });
            fetchCache.set(key, promise);
            return promise;
        },
        [inits],
    );
};
